import { cn } from '@quadralize/utils'
import * as AvatarPrimitive from '@radix-ui/react-avatar'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

const avatarVariants = cva('relative flex shrink-0', {
  variants: {
    size: {
      xs: 'h-6 w-6 text-[10px]',
      sm: 'h-8 w-8 text-xs',
      md: 'h-10 w-10 text-sm',
      lg: 'h-12 w-12 text-base',
      xl: 'h-16 w-16 text-lg',
    },
  },
  defaultVariants: {
    size: 'md',
  },
})

interface AvatarProps
  extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>,
    VariantProps<typeof avatarVariants> {}

const Avatar = React.forwardRef<React.ComponentRef<typeof AvatarPrimitive.Root>, AvatarProps>(
  ({ className, size, children, ...props }, ref) => {
    // Separate Radix primitives from other children (badges)
    const { content, overlays } = React.useMemo(() => {
      const parts = { content: [] as React.ReactNode[], overlays: [] as React.ReactNode[] }
      React.Children.forEach(children, (child) => {
        if (
          React.isValidElement(child) &&
          typeof child.type !== 'string' &&
          'displayName' in child.type &&
          typeof child.type.displayName === 'string' &&
          child.type.displayName.startsWith('Avatar')
        ) {
          parts.content.push(child)
        } else {
          parts.overlays.push(child)
        }
      })
      return parts
    }, [children])

    return (
      <AvatarPrimitive.Root
        ref={ref}
        className={cn(avatarVariants({ size, className }))}
        {...props}
      >
        <div className="overflow-hidden rounded-full border border-border-subtle bg-bg-muted w-full h-full flex items-center justify-center shrink-0">
          {content}
        </div>
        {overlays}
      </AvatarPrimitive.Root>
    )
  },
)
Avatar.displayName = AvatarPrimitive.Root.displayName

const AvatarImage = React.forwardRef<
  React.ComponentRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn('aspect-square h-full w-full object-cover', className)}
    {...props}
  />
))
// Literal names: Avatar sorts its children by displayName, and Radix 1.3 no longer sets one.
AvatarImage.displayName = 'AvatarImage'

const AvatarFallback = React.forwardRef<
  React.ComponentRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      'flex h-full w-full items-center justify-center rounded-full bg-bg-muted font-medium text-text-tertiary',
      className,
    )}
    {...props}
  />
))
AvatarFallback.displayName = 'AvatarFallback'

const AvatarGroup = ({
  className,
  children,
  limit = 4,
  size = 'md',
  ringClassName = 'ring-bg-base',
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  limit?: number
  size?: VariantProps<typeof avatarVariants>['size']
  ringClassName?: string
}) => {
  const avatars = React.Children.toArray(children)
  const displayAvatars = avatars.slice(0, limit)
  const remaining = avatars.length - limit

  return (
    <div className={cn('flex items-center', className)} {...props}>
      {displayAvatars.map((avatar, i) => {
        const isFirst = i === 0
        const isLast = i === displayAvatars.length - 1
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: children don't have unique IDs
          <React.Fragment key={`avatar-group-item-${i}`}>
            {React.isValidElement<AvatarProps>(avatar)
              ? React.cloneElement(avatar, {
                  size: avatar.props.size || size,
                  className: cn(
                    'ring-2 rounded-full',
                    !isFirst && '-ml-3',
                    ringClassName,
                    !isLast && 'relative',
                    avatar.props.className,
                  ),
                  style: { zIndex: displayAvatars.length - i, ...avatar.props.style },
                })
              : avatar}
          </React.Fragment>
        )
      })}
      {remaining > 0 && (
        <Avatar
          size={size}
          className={cn(
            'ring-2 rounded-full relative',
            displayAvatars.length > 0 && '-ml-3',
            ringClassName,
          )}
          style={{ zIndex: 0 }}
        >
          <AvatarFallback className="bg-bg-muted text-[10px] font-bold">
            +{remaining}
          </AvatarFallback>
        </Avatar>
      )}
    </div>
  )
}

export { Avatar, AvatarFallback, AvatarGroup, AvatarImage }
